import {clsx} from 'clsx'
import {
  calculateDiffLineCodeCellGutter,
  cellIdFrom,
  getDiffLineMarkersKey,
  getLineBackgroundColor,
  rowIdFrom,
  trimDiffLineHtml,
} from '../../helpers/line-helpers'
import type {DiffLine, LineKey} from '../../types'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {useMarkersAtPosition} from '@github-ui/pull-requests/pull-request-store-selectors'
import {noop} from '@github-ui/noop'
import {InlineMarkers} from '../conversations/InlineMarkers'
import {useId, useRef, useState} from 'react'
import {StartConversation} from '../conversations/StartConversation'
import {SimpleActionBar} from '../grid-cells/SimpleActionBar'
import {useDialogModeIsEnabled, useDiffAnchor} from '../../stores/selectors'
import {CommentsPreference} from '@github-ui/diff-view-settings/types'
import type {ViewerData} from '../../conversation-types'
import ExpandableHunkHeaderDiffLine from '../ExpandableHunkHeaderDiffLine'
import {getLineHunkData, type HunkData} from '../../helpers/hunk-data-helpers'

const contentColumnIndex = 2

export function SimpleUnifiedDiffRow({
  diffLine,
  nextDiffLine,
  prevDiffLine,
  filePath,
  avatarURL,
  userLogin,
  viewerCanApplySuggestion,
  hunksData,
}: {
  diffLine: DiffLine
  nextDiffLine: DiffLine | undefined
  prevDiffLine: DiffLine | undefined
  filePath: string
  hunksData: HunkData[]
  avatarURL?: string
  userLogin?: string
  viewerCanApplySuggestion?: boolean
}) {
  const isHunkRow = diffLine.type === 'HUNK'
  const showLeftNumber = diffLine.type !== 'ADDITION'
  const showRightNumber = diffLine.type !== 'DELETION'
  const diffAnchor = useDiffAnchor()
  const rowId = rowIdFrom(diffAnchor, diffLine, diffLine)
  const cellId = cellIdFrom(rowId, contentColumnIndex)
  const viewerData = {
    avatarUrl: avatarURL ?? '',
    login: userLogin ?? '',
    viewerCanApplySuggestion: viewerCanApplySuggestion ?? false,
  }

  // TODO: line selection should be different color in future iteration
  // as part of https://github.com/github/pull-requests/issues/21372
  const lineNumberBackgroundColor = getLineBackgroundColor(diffLine.type, true, false)
  const codeBackgroundColor = getLineBackgroundColor(diffLine.type, false, false)
  const [lineHtml, lineTypeCharacter] = trimDiffLineHtml(diffLine.html, diffLine.type)
  const showLineTypeCharacter = lineTypeCharacter && ['+', '-'].includes(lineTypeCharacter)
  const [isNewConversationDialogOpen, setIsNewConversationDialogOpen] = useState(false)
  const [isActionBarVisible, setIsActionBarVisible] = useState(false)
  const {currentHunk, nextHunk, previousHunk} = getLineHunkData(diffLine, hunksData)
  const inlineDialogHeadingId = `inline-dialog-heading-${useId()}`
  const isInDialogMode = useDialogModeIsEnabled(cellId)

  const lineSide = diffLine.type !== 'DELETION' ? 'RIGHT' : 'LEFT'
  const lineNumber = lineSide === 'RIGHT' ? diffLine.right : diffLine.left
  const lineMarkersKey = getDiffLineMarkersKey(lineNumber, lineSide)
  const {threads, annotations, commentsPreference} = useMarkersAtPosition(filePath, lineMarkersKey ?? ('' as LineKey))
  const hasThreads = threads.length + annotations.length !== 0
  const commentIndicatorGutterSize = calculateDiffLineCodeCellGutter({
    hasThreads: hasThreads && commentsPreference === CommentsPreference.Collapsed,
  })

  // TODO: This needs to be reworked in future iteration
  // as part of https://github.com/github/pull-requests/issues/21372
  const inlineMarkersRef = useRef<HTMLDivElement | null>(null)
  const cellRef = useRef<HTMLTableCellElement | null>(null)

  const [showInlineMarkers, setShowInlineMarkers] = useState(threads.length > 0 || annotations.length > 0)
  const isLeftSide = diffLine.type === 'DELETION'

  return (
    <tr className="diff-line-row">
      {isHunkRow ? (
        <ExpandableHunkHeaderDiffLine
          currentHunk={currentHunk}
          diffAnchor={diffAnchor}
          diffLine={diffLine}
          isLeftSide={isLeftSide}
          isSplit={false}
          nextHunk={nextHunk}
          nextLine={nextDiffLine as DiffLine}
          previousHunk={previousHunk}
          prevLine={prevDiffLine as DiffLine}
        />
      ) : (
        <>
          {showLeftNumber ? (
            <td className="text-center v-align-top" style={{backgroundColor: lineNumberBackgroundColor}}>
              <code>{diffLine.left}</code>
            </td>
          ) : (
            <td style={{userSelect: 'none', position: 'relative', backgroundColor: lineNumberBackgroundColor}} />
          )}
          {showRightNumber ? (
            <td className="text-center v-align-top" style={{backgroundColor: lineNumberBackgroundColor}}>
              <code>{diffLine.right}</code>
            </td>
          ) : (
            <td style={{userSelect: 'none', position: 'relative', backgroundColor: lineNumberBackgroundColor}} />
          )}
          <td
            data-grid-cell-id={cellId}
            className="diff-text-cell"
            style={{backgroundColor: codeBackgroundColor}}
            onMouseEnter={() => setIsActionBarVisible(true)}
            onMouseLeave={() => setIsActionBarVisible(false)}
            aria-labelledby={isInDialogMode ? inlineDialogHeadingId : undefined}
            role={isInDialogMode ? 'dialog' : 'gridcell'}
          >
            {isInDialogMode && (
              <h1 id={inlineDialogHeadingId} className="sr-only">
                Comment view
              </h1>
            )}
            <code
              className={clsx('diff-text syntax-highlighted-line', {
                addition: diffLine.type === 'ADDITION',
                deletion: diffLine.type === 'DELETION',
              })}
            >
              {showLineTypeCharacter && <span className="diff-text-marker">{lineTypeCharacter}</span>}
              <VerifiedHTMLDiv html={lineHtml as SafeHTMLString} className="diff-text-inner" />
            </code>
            {showInlineMarkers ? (
              <InlineMarkers
                diffLine={diffLine}
                gridCellId={cellId}
                gutterSizeOffset={commentIndicatorGutterSize}
                inlineMarkersRef={inlineMarkersRef}
                cellRef={cellRef}
                fileAnchor={diffAnchor}
                // passing a noop because in simplified view we are not dealing with collapsed comments
                dispatchMarkersStatus={noop}
                shouldListenForEscapeKey
                lineMarkersKey={lineMarkersKey}
                annotations={annotations}
                conversationListThreads={threads}
                filePath={filePath}
                onCloseFocusMode={noop}
                isRowSelected={false}
                lineType={diffLine.type}
                viewerData={viewerData as ViewerData}
                onCloseConversationList={noop}
                onDeleteLineComment={noop}
              >
                {isNewConversationDialogOpen && (
                  <div
                    className={clsx(
                      ' border rounded-2 color-border-default',
                      threads.length === 0 ? 'mt-2 mb-1' : 'mt-2 mb-1',
                    )}
                  >
                    <StartConversation
                      diffLine={diffLine}
                      diffAnchor={diffAnchor}
                      batchingEnabled
                      filePath={filePath}
                      focusOnMount
                      isLeftSide={lineSide === 'LEFT'}
                      isInDialogMode={false}
                      isOpen={isNewConversationDialogOpen}
                      lineNumber={diffLine.blobLineNumber}
                      viewerData={viewerData}
                      onCloseCommentDialog={() => {
                        setIsNewConversationDialogOpen(false)
                        setShowInlineMarkers(false)
                      }}
                    />
                  </div>
                )}
              </InlineMarkers>
            ) : null}
            {isActionBarVisible && (
              <SimpleActionBar
                openStartConversationDialog={() => {
                  setIsNewConversationDialogOpen(true)
                  setShowInlineMarkers(true)
                }}
                authorAvatarUrl={avatarURL ?? ''}
                authorLogin={userLogin ?? ''}
                ref={cellRef as unknown as React.RefObject<HTMLButtonElement>}
                copilotChatReference={undefined}
                currentHunk={currentHunk}
                diffAnchor={diffAnchor}
                diffLine={diffLine}
                threads={threads}
                annotations={annotations}
                handleCopyCode={noop}
                hasDraftComment={false}
                isLeftSide={lineSide === 'LEFT'}
                isSplit={false}
                nextHunk={nextHunk}
                previousHunk={previousHunk}
                //TODO: make this actually read a user's comment preference
                showStartConversation
                shouldDisplayMinimizedComments={false}
                shouldDisplayCollapseComments
                toggleViewingMarkers={noop}
                viewerData={{
                  avatarUrl: 'string',
                  diffViewPreference: 'string',
                  lineSpacingPreference: 'compact',
                  login: 'string',
                  shouldDisplayComments: true,
                  shouldDisplayEditOptions: true,
                  tabSizePreference: 8,
                  viewerCanComment: true,
                  viewerCanApplySuggestion: true,
                }}
              />
            )}
          </td>
        </>
      )}
    </tr>
  )
}

try{ SimpleUnifiedDiffRow.displayName ||= 'SimpleUnifiedDiffRow' } catch {}